import { LogOut, Megaphone, Settings, Shield, User, Users } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

interface UserDropdownProps {
  isOpen: boolean
  onToggle: () => void
  onClose: () => void
  avatarSrc: string
  user: { name?: string; email?: string } | undefined
  kennelName: string | null
  isKennelAdmin: boolean
  isPermissionsLoading: boolean
  userKennelId: string | null | undefined
  showAdvertiserButton: boolean
  advertiserPath: string
  advertiserLabel: string
  onLogout: () => void
}

export default function UserDropdown({
  isOpen,
  onToggle,
  onClose,
  avatarSrc,
  user,
  kennelName,
  isKennelAdmin,
  isPermissionsLoading,
  userKennelId,
  showAdvertiserButton,
  advertiserPath,
  advertiserLabel,
  onLogout,
}: UserDropdownProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  function go(path: string) {
    navigate(path)
    onClose()
  }

  return (
    <div className="relative user-menu-container">
      <button
        onClick={onToggle}
        className="flex items-center rounded-full transition-transform duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-golden"
        aria-label={t("nav.openUserMenu")}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <img
          src={avatarSrc}
          alt=""
          className="w-9 h-9 rounded-full object-cover border-2 border-main-purple"
          role="presentation"
          referrerPolicy="no-referrer"
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-fit bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50"
          role="menu"
          aria-label={t("nav.userMenu")}
        >
          <div className="px-4 py-2 border-b border-gray-100" role="presentation">
            <p className="text-sm font-medium text-golden truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>

          {isKennelAdmin && !isPermissionsLoading && (
            <button
              onClick={() => go("/painel-admin")}
              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-main-purple hover:bg-purple-50 transition-colors duration-200 focus:outline-none"
              role="menuitem"
            >
              <Shield className="w-4 h-4" aria-hidden="true" />
              <span className="truncate">
                {kennelName ? t("nav.panelWithKennel", { kennelName }) : t("nav.adminPanel")}
              </span>
            </button>
          )}

          {isKennelAdmin && !isPermissionsLoading && userKennelId && (
            <button
              onClick={() => go(`/canis/${userKennelId}`)}
              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-main-purple hover:bg-purple-50 transition-colors duration-200 focus:outline-none"
              role="menuitem"
            >
              <Users className="w-4 h-4" aria-hidden="true" />
              <span className="truncate">{t("nav.associationProfile")}</span>
            </button>
          )}

          {showAdvertiserButton && (
            <button
              onClick={() => go(advertiserPath)}
              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-main-purple hover:bg-purple-50 transition-colors duration-200 focus:outline-none"
              role="menuitem"
            >
              <Megaphone className="w-4 h-4" aria-hidden="true" />
              <span>{advertiserLabel}</span>
            </button>
          )}

          <button
            onClick={() => go("/perfil")}
            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:bg-gray-50"
            role="menuitem"
            aria-label={t("nav.aboutMe")}
          >
            <User className="w-4 h-4" aria-hidden="true" />
            <span>{t("nav.aboutMe")}</span>
          </button>

          <button
            onClick={() => go("/definicoes")}
            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:bg-gray-50"
            role="menuitem"
            aria-label={t("nav.userSettings")}
          >
            <Settings className="w-4 h-4" aria-hidden="true" />
            <span>{t("nav.settings")}</span>
          </button>

          <button
            onClick={onLogout}
            className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200 focus:outline-none focus:bg-red-50"
            role="menuitem"
            aria-label={t("nav.logoutAccount")}
          >
            <LogOut className="w-4 h-4" aria-hidden="true" />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      )}
    </div>
  )
}
